import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { type CellValueChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCustomerCenter } from 'hooks/useCustomerCenter'
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { validateNumberLength, validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef, IRichCellEditorParams, ValueFormatterParams, ValueParserParams } from 'ag-grid-enterprise'
import type { CustomerCenter, MstCustomerCenter, GetMstCustomersCenterCustomerCenterGetRequest, MstCustomer, MstCenter } from 'api-client'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  org_customer_id: 'orgCustomerId',
  org_customer_center_id: 'orgCustomerCenterId',
  customer_center_name: 'customerCenterName',
  customer_center_address: 'customerCenterAddress',
  customer_center_gln: 'customerCenterGln',
  transport_org_center_id: 'transportOrgCenterId',
  remarks: 'remarks',
  delete_flg: 'deleteFlg',
}
const primayKey = ['orgCustomerId', 'orgCustomerCenterId']

interface DataListProps extends DataListPropsBase {
  list: CustomerCenter
  customer: MstCustomer[]
  center: MstCenter[]
  searchParam: GetMstCustomersCenterCustomerCenterGetRequest
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

interface MstCustomerCenterWithId extends MstCustomerCenter, RecordEditEx {}

function DataList({
  list,
  customer,
  center,
  isLoading,
  searchParam,
  onReload,
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const { fetchCustomerCentersUpdate, isUpdating, result, customerCenterSearchResult } = useCustomerCenter()
  const internalGridRef = useRef<AgGridReact<MstCustomerCenterWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  const searchParamCurrent = useRef<GetMstCustomersCenterCustomerCenterGetRequest>(searchParam)

  useEffect(() => {
    searchParamCurrent.current = searchParam
  }, [searchParam])

  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const [editorDataReady, setEditorDataReady] = useState(false)
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [row, setRow] = useState<MstCustomerCenterWithId[]>(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
  const [rowChangeTriggerRowID, setRowChangeTriggerRowID] = useState<string | null>(null);
  const [customerSelect, setCustomerSelect] = useState<(string | null)[]>([])

  // use refs to store latest values to avoid stale closure
  const customerRef = useRef(customer)
  const centerRef = useRef(center)

  useEffect(() => {
    customerRef.current = customer
    centerRef.current = center
  }, [customer, center])

  useEffect(() => {
    if (customerSelect.length > 0) {
      setEditorDataReady(true)
    }
  }, [customerSelect])

  useEffect(() => {
    const values = customer?.map((r) => r.orgCustomerId) || []
    setCustomerSelect(values)
  }, [customer])

  useEffect(() => {
    // Set editorDataReady when center data is available
    if (center.length > 0) {
      setEditorDataReady(true)
    }
  }, [center])

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    setResultMessage(customerCenterSearchResult)
  }, [customerCenterSearchResult])

  // Helper function to get available centers based on customer's internalTransferFlg
  const getAvailableCenters = useCallback((orgCustomerId: string | null) => {
    if (!orgCustomerId) return []
    
    const currentCustomer = customer.find(c => c.orgCustomerId === orgCustomerId)
    if (currentCustomer?.deleteFlg === true) return []
    if (currentCustomer?.internalTransferFlg === true) {
      // If customer has internalTransferFlg=true, only show centers with internalInboundFlg=true
      return center.filter(c => c.internalInboundFlg === true)
    }
    return []
  }, [customer, center])

  const createBlankRow = useCallback((): MstCustomerCenterWithId => {
    const blankRow: Partial<MstCustomerCenterWithId> = {
      companyId: loginUserInfo?.companyId || null,
      orgCustomerId: searchParamCurrent.current.orgCustomerId,
      orgCustomerCenterId: null,
      customerCenterName: null,
      customerCenterAddress: null,
      customerCenterGln: null,
      customerCenterId: null,
      transportOrgCenterId: null,
      remarks: null,
      deleteFlg: false,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      ...initRecordEditEx(true),
    }
    return blankRow as MstCustomerCenterWithId
  }, [])

  // 入力チェック
  const fieldValidate = (pRow: MstCustomerCenterWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field) {
      case 'orgCustomerId': 
      case 'orgCustomerCenterId':
      {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break
      }
      case 'customerCenterName': {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        break
      }
      case 'customerCenterGln': {
        hasErrors = checkAndSetError(pRow, field, validateNumberLength(newValue, 9).join(','))
        break
      }
      case 'transportOrgCenterId': {
        checkAndSetError(pRow, field, null)
        if (newValue) {
          // 同一コードは他の得意先センターに設定できない
          // gridApiから最新のデータを取得して重複チェック
          let duplicateFound = false
          gridRef.current?.api?.forEachNode((node) => {
            if (node.data && node.data.id !== pRow.id && node.data.transportOrgCenterId === newValue) {
              duplicateFound = true
            }
          })
          if (duplicateFound) {
            hasErrors = checkAndSetError(pRow, field, "同一コードは他の得意先センターに設定できません")
          } else {
            const currentCustomer = customerRef.current.find(c => c.orgCustomerId === pRow.orgCustomerId)
            if (currentCustomer?.internalTransferFlg === true) {
              const selectedCenter = centerRef.current.find(c => c.orgCenterId === newValue)
              if (selectedCenter && selectedCenter.internalInboundFlg !== true) {
                hasErrors = checkAndSetError(pRow, field, "社内移送フラグがtrueの場合、内部入荷フラグがtrueのセンターのみ選択可能です")
              }
            } else {
              hasErrors = checkAndSetError(pRow, field, "社内移送フラグがtrueの場合、内部入荷フラグがtrueのセンターのみ選択可能です")
            }
          }
        }
        break
      }
      default:
        return false
    }
    // console.log('fieldValidate', field, pRow, hasErrors)
    return hasErrors
  }

  const cellValueChanged = (event: CellValueChangedEvent<MstCustomerCenterWithId>, hasError: boolean) => {
    const { data, colDef } = event
    const { field } = colDef

    const updatedRow = { ...data }
    
    // transportOrgCenterIdが変更された場合、他の行も再検証
    if (field === "transportOrgCenterId") {
      const gridApi = gridRef.current?.api
      if (gridApi && !gridApi.isDestroyed()) {
        const rowsToUpdate: MstCustomerCenterWithId[] = []
        gridApi.forEachNode((rowNode) => {
          if (rowNode.data && rowNode.data.id !== updatedRow.id && rowNode.data.transportOrgCenterId) {
            const otherRow = rowNode.data
            fieldValidate(otherRow, "transportOrgCenterId", otherRow.transportOrgCenterId)
            rowsToUpdate.push(otherRow)
          }
        })
        if (rowsToUpdate.length > 0) {
          gridApi.applyTransaction({ update: rowsToUpdate })
        }
      }
    }
  }

  useEffect(() => {
    // 値変更時に、useStateの値を参照する処理を行う場合
    // まとめてコピペした場合など、1回しか発生しないので、変更行はまとめて設定する
    if (!rowChangeTriggerRowID) return
    // console.log("cellChangeTrigger", rowChangeTriggerRowID, items.length)
    const updatedRows: MstCustomerCenterWithId[] = [];
    gridRef.current?.api?.forEachNode((node) => {
      if (node?.data) {
        const trRow = node.data as MstCustomerCenterWithId
        // console.log("cellChangeTrigger",trRow.isEdited, `'${trRow.orgCenterId}'`,searchParam.orgItemCode)
        if (trRow.isEdited) {
          // 新規行にセンターIDが入っていない場合は、検索条件のセンターIDをセット
          if (!trRow.orgCustomerId) {
            const searchCenterDefault = customer.find((rowCenter) => rowCenter.orgCustomerId === searchParam.orgCustomerId)
            trRow.orgCustomerId = searchCenterDefault?.orgCustomerId || null
          }
          // 商品マスタの値を設定
          // node.setData(trRow)
          updatedRows.push(trRow)
        }
      }
    })
    if (updatedRows.length > 0) gridRef.current?.api?.applyTransaction({ update: updatedRows })
    // 必要な処理が終わったら、NULLに戻すこと！
    setRowChangeTriggerRowID(null);
  }, [rowChangeTriggerRowID])

  const updateMstCustomerCenter = useCallback(
    async (updatedRows: MstCustomerCenterWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, editedFields, errors, ...rest }) => ({
        ...rest,
      }))

      try {
        await fetchCustomerCentersUpdate({
          mstCustomerCenterUpdate: { body: datalist },
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === 'error') {
          const gridApi = gridRef.current?.api

          err.errors.forEach((errrow: any) => {
            const { rowdata } = errrow
            const orgCustomerId = (rowdata as { org_customer_id: string | null })?.org_customer_id
            const orgCustomerCenterId = (rowdata as { org_customer_center_id: string | null })?.org_customer_center_id
            const errRow = updatedRows.find((rowdatas) => 
              rowdatas.orgCustomerId === orgCustomerId && 
              rowdatas.orgCustomerCenterId === orgCustomerCenterId
            )
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { 
                orgCustomerId: [errrow.errorMessage],
                orgCustomerCenterId: [errrow.errorMessage] 
              }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    },
    [fetchCustomerCentersUpdate, searchParam]
  )

  const handleImportExcel = (data: any[]) => {
    let hasError = false
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCustomerCenterWithId> = {}
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue = importvalue || null

          if ( [ 'deleteFlg'].includes(tableField)) {
            setvalue = parseBoolean(importvalue)
          } else if (['transportOrgCenterId'].includes(tableField)) {
            // Validate against center data based on customer's internalTransferFlg
            const validCenter = center.find(c => c.orgCenterId === importvalue)
            if (validCenter) {
              // Check if customer has internalTransferFlg=true
              const currentCustomer = customer.find(c => c.orgCustomerId === mappedRow.orgCustomerId)
              if (currentCustomer?.internalTransferFlg === true) {
                // If customer has internalTransferFlg=true, center must have internalInboundFlg=true
                if (validCenter.internalInboundFlg === true) {
                  setvalue = importvalue
                } else {
                  hasError = true
                }
              } else {
                // If customer has internalTransferFlg=false, any center is allowed
                setvalue = importvalue
              }
            } else if (importvalue) {
              hasError = true
            }
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstCustomerCenterWithId] = setvalue as unknown as undefined
        }
      })
      return mappedRow as MstCustomerCenterWithId
    })

    // Validate duplicate transportOrgCenterId after parsing all data
    const transportOrgCenterIds = new Set<string>()
    mappedData.forEach((record, index) => {
      if (record.transportOrgCenterId) {
        if (transportOrgCenterIds.has(record.transportOrgCenterId)) {
          hasError = true
        } else {
          transportOrgCenterIds.add(record.transportOrgCenterId)
        }
      }
    })

    return { mappedData, hasError }
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    clearPinnedRows,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    updateData: updateMstCustomerCenter,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    cellValueChanged,
    storageKey: 'gridColumnState:/master-customer-center',
    scrollPositionKey: 'orgCustomerCenterId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '得意先企業ID',
      field: 'orgCustomerId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 300,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        selectValueFormatter(params.value, customer, 'orgCustomerId', 'customerName'),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCustomerId, customer, "orgCustomerId", "customerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, customer, 'orgCustomerId'),
      cellEditorParams: editorDataReady
        ? ({
            values: [null, ...customerSelect],
            formatValue: (value: string) => selectValueFormatter(value, customer, 'orgCustomerId', 'customerName'),
            searchType: 'matchAny',
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams)
        : { values: ['-'] },
    },
    {
      headerName: '得意先センターID',
      field: 'orgCustomerCenterId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 200,
    },
    {
      headerName: 'センター名',
      field: 'customerCenterName',
      editable: isEnableEdit(),
      pinned: 'left',
      width: 300,
      valueParser: parseStringValue,
    },
    {
      headerName: 'センター所在地住所',
      field: 'customerCenterAddress',
      editable: isEnableEdit(),
      flex: 1,
      valueParser: parseStringValue,
    },
    {
      headerName: 'GS1事業所識別コード',
      field: 'customerCenterGln',
      editable: isEnableEdit(),
      flex: 1,
      valueParser: parseStringValue,
    },
    {
      headerName: '移送先自社センターID',
      field: 'transportOrgCenterId',
      flex: 1,
      editable: isEnableEdit(),
      cellClassRules: getCellClassRules(),
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      valueParser: (params: ValueParserParams) =>
        selectValueParser(params, center, 'orgCenterId'),
      filter: 'agMultiColumnFilter',
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) =>
          selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      },
      cellEditorParams: (params: any) => {
        if (!editorDataReady) return { values: ['-'] }
        const orgCustomerId = params.data?.orgCustomerId
        let internalTransferFlg = true
        if (orgCustomerId) {
          const currentCustomer = customer.find(c => c.orgCustomerId === orgCustomerId)
          if (currentCustomer) {
            internalTransferFlg = currentCustomer.internalTransferFlg || false
          }
        }
        const availableCenters = internalTransferFlg ? getAvailableCenters(orgCustomerId) : []
        return {
          values: [null, ...availableCenters.map(c => c.orgCenterId)],
          formatValue: (value: string) =>
            selectValueFormatter(value, center, 'orgCenterId', 'centerName'),
          searchType: 'matchAny',
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
        } as IRichCellEditorParams
      },
    },
    {
      headerName: 'PSI得意先センターID',
      field: 'customerCenterId',
      flex: 1,
    },
    {
      headerName: '備考',
      field: 'remarks', 
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
      flex: 1,
    },
    {
      headerName: '削除',
      field: 'deleteFlg',
      flex: 1,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue,
      editable: isEnableEdit(),
    },
  ]

  const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [customer, center, editorDataReady])

  // columnDefsが再計算された際に、保存済みのカラム表示設定を再適用する
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    try {
      const savedAll = localStorage.getItem('gridColumnStates')
      if (savedAll) {
        const obj = JSON.parse(savedAll) as Record<string, any>
        const state = obj?.['gridColumnState:/master-customer-center']
        if (Array.isArray(state) && state.length > 0) {
          // requestAnimationFrameでAG GridがcolumnDefsを処理した後に適用
          requestAnimationFrame(() => {
            if (!api.isDestroyed()) {
              api.applyColumnState({ state, applyOrder: true })
            }
          })
        }
      }
    } catch { /* noop */ }
  }, [columnDefs])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        { headerName: '得意先企業ID', children: [{ headerName: 'org_customer_id', field: 'orgCustomerId', cellClass: 'stringType' }] },
        { headerName: '得意先センターID', children: [{ headerName: 'org_customer_center_id', field: 'orgCustomerCenterId', cellClass: 'stringType' }] },
        { headerName: 'センター名', children: [{ headerName: 'customer_center_name', field: 'customerCenterName', cellClass: 'stringType' }] },
        { headerName: 'センター所在地住所', children: [{ headerName: 'customer_center_address', field: 'customerCenterAddress', cellClass: 'stringType' }] },
        { headerName: 'GS1事業所識別コード', children: [{ headerName: 'customer_center_gln', field: 'customerCenterGln', cellClass: 'stringType' }] },
        { headerName: '移送先自社センターID', children: [{ headerName: 'transport_org_center_id', field: 'transportOrgCenterId', cellClass: 'stringType' }] },
        { headerName: 'PSI得意先センターID', children: [{ headerName: 'customer_center_id', field: 'customerCenterId', cellClass: 'stringType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
      ])

  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
      clearPinnedRows()
    }
  }, [list.list])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
